import { captureException } from '@sentry/react'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useForm } from 'react-hook-form'

import { useSchoolRequestFields } from './hooks'
import styles from './school-request-waitlist.module.scss'
import { Heading } from '@components/heading'
import { TelephoneInput } from '@components/input/telephone-input'
import { TextInput } from '@components/input/text-input'
import type { FormProps } from '@contentful-components/forms/props'
import { useDevMenuItem } from '@lib/hooks/useDevMenuItem'
import { DevActionGroup, DevActionName } from '@lib/store/ui/dev-menu'
import type { ContactInfo } from '@lib/store/waitlist/WaitlistStore'
import { Waitlist, waitlistStore } from '@lib/store/waitlist/WaitlistStore'
import { LINK } from '@shared/util/link'
import { telephoneToE164 } from '@shared/util/phone'
import { loadTestApiFactory } from '@shared/util/tests'

const success = LINK.schoolRequest('success').serialize()
const fail = LINK.schoolRequest('fail').serialize()

/**
 * This form lets the user enter their contact info to join the school request waitlist.
 */
export const SchoolRequestWaitlist: FunctionComponent<FormProps<ContactInfo>> =
    observer(({ title, subtitle, renderSubmit }) => {
        const router = useRouter()

        const { handleSubmit, control, setValue, setError, getValues } =
            useForm<ContactInfo>({
                mode: 'onTouched',
                defaultValues: waitlistStore.contactInfo,
            })

        const { register, fields } = useSchoolRequestFields(control)

        useDevMenuItem({
            name: DevActionName.FILL_FORM,
            group: DevActionGroup.PAGE,
            async onActivate() {
                const testApiFactory = await loadTestApiFactory()
                const [randomEmail, randomPhone, randomName, randomAddress] =
                    await Promise.all([
                        testApiFactory.getUnlinkedEmail(),
                        testApiFactory.getUnlinkedPhoneNumber(),
                        testApiFactory.generateName(),
                        testApiFactory.generateAddress(),
                    ])

                const options = { shouldValidate: true }

                setValue('name', randomName.fullName, options)
                setValue('email', randomEmail, options)
                setValue('phone', randomPhone, options)
                setValue('school', `${randomName.fullName} Elementary`, options)
                setValue(
                    'cityState',
                    `${randomAddress.city}, ${randomAddress.stateProvinceRegion}`,
                    options,
                )
                setValue('facultyAdvisor', randomName.fullName, options)

                waitlistStore.setContactInfo(getValues())
            },
        })

        return (
            <form
                onChange={() => waitlistStore.setContactInfo(getValues())}
                onSubmit={handleSubmit(async (values) => {
                    if (typeof values?.phone !== 'string') {
                        setError('phone', {
                            message: fields.phone.errorTexts.validate,
                            type: 'validate',
                        })
                        return
                    }

                    const phone = await telephoneToE164(values.phone)

                    if (!phone) {
                        setError('phone', {
                            message: fields.phone.errorTexts.validate,
                            type: 'validate',
                        })
                        return
                    }

                    try {
                        await waitlistStore.joinWaitlistFirebase(
                            Waitlist.SchoolRequest,
                            values,
                        )
                    } catch (e) {
                        captureException(e)
                        router.push(fail)
                        return
                    }

                    router.push(success)
                })}
            >
                <Heading level={1}>{title}</Heading>

                {subtitle}

                <TextInput
                    data-testid='school-request.name'
                    {...register('name')}
                />

                <TextInput
                    data-testid='school-request.email'
                    {...register('email')}
                />

                <TelephoneInput
                    data-testid='school-request.phone'
                    {...register('phone')}
                />

                <TextInput
                    data-testid='school-request.school'
                    {...register('school')}
                />
                <TextInput
                    data-testid='school-request.cityState'
                    {...register('cityState')}
                />
                <TextInput
                    data-testid='school-request.facultyAdvisor'
                    {...register('facultyAdvisor')}
                />

                <div className={styles.submit}>{renderSubmit(control)}</div>
            </form>
        )
    })
